import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Input } from '@angular/core';
import { Router, ActivatedRoute, } from '@angular/router';

import { Store } from '@ngrx/store';

import { Subject, Observable, of, zip } from 'rxjs';
import { takeUntil, catchError, map, switchMap } from 'rxjs/operators';

import * as _ from 'lodash';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';

import { Customer } from 'modules/shared/models/customer.models';
import { GetCrpsPayload } from 'store/models/crp.models';
import { Crp } from 'modules/shared/models/crp.models';
import { CrpResponse } from 'store/models/crp.models';
import { CurrentUser } from 'modules/shared/models/account.models';
import { Role } from 'modules/shared/models/role.models';

import { CustomerService } from 'modules/core/services/customer.service';
import { DialogService } from 'modules/shared/components/dialog/dialog.service';
import { CrpService } from 'modules/core/services/crp.service';
import { SnackbarService } from 'app/services/snackbar.service';

import { environment } from 'environments/environment';

@Component({
  selector: 'artemis-customer-overview-card',
  templateUrl: './customer-overview-card.component.html',
  styleUrls: ['./customer-overview-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerOverviewCardComponent implements OnInit, OnDestroy {

  @Input() showCustomerParticulars: boolean = false;

  private unsubscribe$: Subject<any> = new Subject<any>();

  isUserOnlyL1$: Observable<boolean>;

  customerId: number;
  currentCustomer: Customer;
  currentCustomerStatus: string;
  currentUser: CurrentUser;
  currentUserRoles: Array<Role>;

  downloadDropdownOpen: boolean = false;

  isIncomplete$: Observable<boolean> =
    this.store.select(Selectors.customer.getCrps)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((res: CrpResponse) => this.crpService.getSearchableCrps(res.results)),
        map((crps: Crp[]) => {
          if (crps.length === 0) {
            return true;
          }
          return !crps.every((crp: Crp) => crp.latestScreeningConclusion && !crp.latestScreeningConclusion.invalid);
        })
      );

  loading$: Observable<boolean>;
  customerParticularsLoading: boolean;

  allowDeleteCustomer: boolean = environment['ALLOW_DELETE_CUSTOMER'];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<AppState>,
    private cdRef: ChangeDetectorRef,
    private customerService: CustomerService,
    private snackBar: SnackbarService,
    private dialogService: DialogService,
    private crpService: CrpService,
  ) {
    this.isUserOnlyL1$ = this.store
      .select(Selectors.auth.getUserRoles)
      .pipe(map((roles: Array<Role>) => {
        return roles && !roles.some((role: Role) => role.name === 'L2' || role.name === 'L3');
      }));

  }

  ngOnInit(): void {
    this.getLoadingStatus();

    this.customerId = this.activatedRoute.snapshot.params['id'];
    this.store.dispatch(new RootActions.customer.GetCustomer({ customerId: this.activatedRoute.snapshot.params['id'] }));

    this.initUserInfo();
    this.initCurrentCustomer();
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading$ = this.store.select(Selectors.customer.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((customerLoading: Array<CustomerActionTypes>) => {
          return customerLoading.includes(CustomerActionTypes.GET_CUSTOMER) ||
            customerLoading.includes(CustomerActionTypes.GET_CRPS);
        })
      );
  }

  private initUserInfo(): void {
    this.store.select(Selectors.auth.getUserRoles)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (roles: Array<Role>) => {
          this.currentUserRoles = roles;
        }
      );

    this.store.select(Selectors.auth.getUser)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((user: CurrentUser) => {
        this.currentUser = user;
        this.cdRef.markForCheck();
      });
  }

  private initCurrentCustomer(): void {
    this.store.select(Selectors.customer.getCustomerStatus)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((res: any) => {
        this.currentCustomerStatus = res;
        this.cdRef.markForCheck();
      });
    this.store.select(Selectors.customer.getCustomer)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((res: any) => {
        if (res !== null) {
          this.currentCustomer = res;
          this.currentCustomerStatus = res.customerStatus;
          this.store.dispatch(new RootActions.customer.GetCrps(<GetCrpsPayload>{
            customerId: this.currentCustomer.id,
            quietUpdate: true,
            limit: 999
          }));
          this.cdRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Handles when customer particulars component emit new loading status
   * @method customerParticularsLoadingStatusHandler
   */
  customerParticularsLoadingStatusHandler(loadingStatus: boolean): void {
    this.customerParticularsLoading = loadingStatus;
  }

  downloadReport(): void {
    if (this.currentCustomerStatus !== 'PENDING' && this.currentCustomerStatus !== 'DRAFT' && this.currentCustomerStatus !== 'REQUEST CHANGES') {
      this.customerService
        .downloadReport(this.customerId)
        .subscribe((res: any) => {
          const url = window.URL.createObjectURL(new Blob([res]));
          const link = document.createElement('a');
          const fileName = `Detailed Report - ${this.currentCustomer.name}.pdf`;
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        },
          (error: any) => {
            this.snackBar.error('Error: Failed to download detailed report.');
          });
    }
  }

  downloadAcceptanceForm(): void {
    this.customerService
      .downloadAcceptanceForm(this.customerId)
      .subscribe((res: any) => {
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement('a');
        const fileName = `Customer Acceptance Form - ${this.currentCustomer.name}.pdf`;
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      },
        (error: any) => {
          this.snackBar.error('Error: Failed to download customer acceptance form.');
        });
  }

  /**
   * Handles when user click delete customer button
   * @method deleteCustomer
   */
  deleteCustomer() {
    this.dialogService
      .confirm(`Are you sure you want to delete customer ${this.currentCustomer.name}?`,
        'DELETE CUSTOMER',
        false,
        true)
      .pipe(
        switchMap((dialogAction: string) => {
          if (dialogAction === 'submit') {
            return zip(of(dialogAction), this.customerService.deleteCustomer(this.currentCustomer.id));
          } else {
            return zip(of(dialogAction), of(null));
          }
        }),
        catchError((error: any) => of(error)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([dialogAction, res]: [string, any]) => {
        if (dialogAction === 'submit') {
          if (res.status === 204) {
            this.snackBar.success(`Customer successfully deleted`);
            this.router.navigate(['/customers']);
          } else {
            this.snackBar.error('Network error. Please try again.');
          }
        }
      });
  }
}
